import type { SaveData, PlayerState } from '../types';
import { eventBus } from '../events/EventBus';

// ============================================
// SaveSystem - Sistema de guardado del juego
// ============================================

const SAVE_KEY = 'dead-frequency-save';
const SAVE_VERSION = 1;

export class SaveSystem {
  private apiBaseUrl: string;

  constructor() {
    this.apiBaseUrl = import.meta.env.VITE_API_URL || '/api';
  }

  async init(): Promise<void> {
    console.log('[SaveSystem] Inicializado');
  }

  async save(data: SaveData): Promise<boolean> {
    try {
      // Guardar localmente como respaldo
      localStorage.setItem(SAVE_KEY, JSON.stringify(data));

      // Intentar guardar en el servidor si está disponible
      if (this.isOnline()) {
        await this.saveToServer(data);
      }

      eventBus.emit('save:write', undefined);
      return true;
    } catch (error) {
      console.error('[SaveSystem] Error al guardar:', error);
      eventBus.emit('save:error', String(error));
      return false;
    }
  }

  async load(): Promise<SaveData | null> {
    try {
      // Intentar cargar del servidor primero
      if (this.isOnline()) {
        const serverData = await this.loadFromServer();
        if (serverData) {
          return serverData;
        }
      }

      // Cargar localmente como respaldo
      const localData = localStorage.getItem(SAVE_KEY);
      if (localData) {
        return JSON.parse(localData) as SaveData;
      }

      return null;
    } catch (error) {
      console.error('[SaveSystem] Error al cargar:', error);
      return null;
    }
  }

  async clear(): Promise<void> {
    localStorage.removeItem(SAVE_KEY);
    if (this.isOnline()) {
      try {
        await fetch(`${this.apiBaseUrl}/save`, { method: 'DELETE' });
      } catch {
        // Ignorar errores de conexión
      }
    }
  }

  private async saveToServer(data: SaveData): Promise<void> {
    await fetch(`${this.apiBaseUrl}/save`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  private async loadFromServer(): Promise<SaveData | null> {
    const response = await fetch(`${this.apiBaseUrl}/save`);
    if (response.ok) {
      return (await response.json()) as SaveData;
    }
    return null;
  }

  private isOnline(): boolean {
    return navigator.onLine;
  }

  createDefaultSave(playerState: PlayerState): SaveData {
    return {
      version: SAVE_VERSION,
      timestamp: Date.now(),
      playerState,
      discoveredClues: [],
      solvedPuzzles: [],
      gameTime: 0,
    };
  }
}
